import React from 'react';
import { Select } from '../../ui/components/Select';

/**
 * Rarity methodology options for the dropdown.
 * These match the PostgreSQL enum values and are ordered by commonality.
 */
const RARITY_METHODOLOGIES = [
  {
    value: 'TRAIT_SCORE',
    label: 'Trait Score (Rarity Tools)',
    description: 'Industry standard method. Higher scores = rarer NFTs.',
  },
  {
    value: 'AVERAGE_PERCENTAGE',
    label: 'Average Percentage (XRP Cafe)',
    description: 'Average of trait percentages. Used by XRP Cafe.',
  },
  {
    value: 'STANDARD',
    label: 'Standard Rank',
    description: 'Sum of trait ranks. Lower total rank = rarer.',
  },
  {
    value: 'STATISTICAL',
    label: 'Statistical (Z-Score)',
    description: 'Uses z-scores to measure trait rarity.',
  },
  {
    value: 'AVERAGE',
    label: 'Average Rank',
    description: 'Like Standard but averages tied ranks.',
  },
  {
    value: 'NONE',
    label: 'No Rarity',
    description: 'Disables rarity calculation.',
  },
] as const;

export type RarityMethodology = typeof RARITY_METHODOLOGIES[number]['value'];

interface RarityMethodologySelectProps {
  value: RarityMethodology;
  onChange: (methodology: RarityMethodology) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Dropdown component for selecting a rarity calculation methodology.
 * Used in the admin collection management interface.
 */
export function RarityMethodologySelect({
  value,
  onChange,
  disabled = false,
  className = '',
}: RarityMethodologySelectProps) {
  const handleChange = (newValue: string) => {
    onChange(newValue as RarityMethodology);
  };

  return (
    <div className={className}>
      <Select
        value={value}
        onValueChange={handleChange}
        disabled={disabled}
        placeholder="Select methodology..."
      >
        {RARITY_METHODOLOGIES.map((methodology) => (
          <Select.Item key={methodology.value} value={methodology.value}>
            <div className="flex flex-col">
              <span className="font-medium">{methodology.label}</span>
              <span className="text-xs text-subtext-color">
                {methodology.description}
              </span>
            </div>
          </Select.Item>
        ))}
      </Select>
    </div>
  );
}

/**
 * Get display name for a methodology value.
 */
export function getMethodologyLabel(value: RarityMethodology): string {
  const methodology = RARITY_METHODOLOGIES.find((m) => m.value === value);
  return methodology?.label ?? value;
}

/**
 * Get description for a methodology value.
 */
export function getMethodologyDescription(value: RarityMethodology): string {
  const methodology = RARITY_METHODOLOGIES.find((m) => m.value === value);
  return methodology?.description ?? '';
}
